<template>
  <!-- Stock gauge header -->
  <div class="mb-4">
    <div class="flex items-end justify-between mb-1.5">
      <div class="flex items-baseline gap-2">
        <span class="text-2xl font-bold" :class="stockValueClass">{{ currentQty ?? '—' }}</span>
        <span v-if="totalQty != null && !isConsumable" class="text-sm text-[var(--color-muted)]">{{ $t('items.denomAvailable', { count: totalQty }) }}</span>
        <span v-else-if="isConsumable" class="text-sm text-[var(--color-muted)]">{{ $t('items.inStockLabel') }}</span>
      </div>
      <div class="flex items-center gap-3 text-xs text-[var(--color-muted)]">
        <span v-if="isConsumable && restockThreshold != null" class="flex items-center gap-1">
          <span class="inline-block w-2.5 h-2.5 rounded-sm border-2 border-amber-500/60"></span>
          {{ $t('items.restockAt', { threshold: restockThreshold }) }}
        </span>
        <span v-if="isBelowThreshold" class="text-amber-400 font-semibold">{{ $t('items.lowStock') }}</span>
      </div>
    </div>

    <!-- Gauge bar -->
    <div v-if="totalQty != null && totalQty > 0" class="relative h-3 rounded-full overflow-hidden bg-gray-800">
      <div
        class="h-full rounded-full transition-all duration-500"
        :class="gaugeClass"
        :style="{ width: Math.min(100, gaugePercent) + '%' }"
      />
      <!-- Restock threshold marker for consumable -->
      <div
        v-if="isConsumable && restockThreshold != null && totalQty > 0"
        class="absolute top-0 bottom-0 w-0.5 bg-amber-500/70"
        :style="{ left: Math.min(100, (restockThreshold / totalQty) * 100) + '%' }"
      />
    </div>
  </div>

  <!-- Timeline (reuses ItemAvailabilityTimeline) -->
  <ItemAvailabilityTimeline v-if="checkouts.length" :checkouts="checkouts" :future-days="futureDays" />
  <div v-else class="state-empty text-sm py-2">{{ $t('items.noCheckoutHistory') }}</div>
</template>

<script setup>
import { computed } from 'vue'
import ItemAvailabilityTimeline from './ItemAvailabilityTimeline.vue'

const props = defineProps({
  checkouts:        { type: Array,  default: () => [] },
  futureDays:       { type: Number, default: 90 },
  /** Current available quantity (displayed as the main number) */
  currentQty:       { type: Number, default: null },
  /** Total / max quantity for the gauge denominator. For consumable this is the original stock. */
  totalQty:         { type: Number, default: null },
  restockThreshold: { type: Number, default: null },
  isConsumable:     { type: Boolean, default: false },
})

const gaugePercent = computed(() => {
  if (props.totalQty == null || props.totalQty === 0) return 0
  return (props.currentQty / props.totalQty) * 100
})

const isBelowThreshold = computed(
  () => props.restockThreshold != null && props.currentQty != null && props.currentQty <= props.restockThreshold,
)

const gaugeClass = computed(() => {
  if (isBelowThreshold.value) return 'bg-amber-500'
  if (gaugePercent.value <= 25) return 'bg-red-500'
  if (gaugePercent.value <= 50) return 'bg-yellow-500'
  return 'bg-emerald-500'
})

const stockValueClass = computed(() => {
  if (isBelowThreshold.value) return 'text-amber-400'
  if (gaugePercent.value <= 25) return 'text-red-400'
  return 'text-emerald-400'
})
</script>
